import { selectTagsStandardProps } from "@/utils/componentUtils";
import { Select } from "antd";
import { useTranslation } from "react-i18next";
import { useIsCurrentAgentTypePlus } from "../../../auth/hooks";
import { CommissionKind, CommissionKindCategory, commissionKindTMap } from "../enums";
import { getCommissionKindCategory } from "../utils";
import { CommissionKindTag } from "./CommissionKindTag";

type Props<T extends boolean> = {
  value?: T extends true ? CommissionKind[] : CommissionKind;
  isMultiple?: T;
  onChange?: (data: T extends true ? CommissionKind[] : CommissionKind) => void;
  style?: React.CSSProperties;
  allowClear?: boolean;
  excluded?: CommissionKind[];
};

export const CommissionKindSelect = <T extends boolean = false>({
  value,
  isMultiple,
  style,
  onChange,
  allowClear,
  excluded,
}: Props<T>) => {
  const { t } = useTranslation();

  const isTopAgentPlus = useIsCurrentAgentTypePlus();

  const options = (
    isTopAgentPlus ? Object.values(CommissionKind) : [CommissionKind.INITIAL, CommissionKind.SUBSEQUENT]
  ).filter((item) => !excluded?.includes(item));

  const categories: CommissionKindCategory[] = [
    CommissionKindCategory.INITIAL,
    CommissionKindCategory.SUBSEQUENT,
    ...(isTopAgentPlus ? [CommissionKindCategory.RENEWAL, CommissionKindCategory.BONUS] : []),
  ];

  return (
    <Select
      style={style}
      {...selectTagsStandardProps(commissionKindTMap)}
      options={categories
        .map((category) => {
          const filteredOptions = options
            .filter((option) => getCommissionKindCategory(option) === category)
            .map((kind) => ({
              value: kind,
              label: <CommissionKindTag kind={kind} />,
            }));

          if (filteredOptions.length === 0) {
            return;
          }

          return {
            label: t(`commissions.batch.enums.commissionKind.${category}`),
            options: filteredOptions,
          };
        })
        .filter((item) => !!item)}
      value={value}
      allowClear={allowClear}
      mode={isMultiple ? "multiple" : undefined}
      maxTagCount="responsive"
      tagRender={(props) => (
        <CommissionKindTag
          kind={props.value}
          closable={props.closable}
          onClose={props.onClose}
          label={props.label}
          style={{ marginRight: 8 }}
        />
      )}
      onChange={onChange}
    />
  );
};
